import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import './index.less';

import { IngestionArtifactType, IngestionResourceModel } from '../../../../../../gamma-api';
import { ContentRenderMode } from '../FragmentList';
import IbIcon from '../../../../../components/common/IbIcon';
import { TEXT_HTML_MIME } from '../../const';
import IbContextMenu from '../../../../../components/common/IbContextMenu';
import IbButton from '../../../../../components/common/IbButton';
import Switcher from '../../../../../components/common/Switcher';

import { MAIN_CLASS_NAME, TITLE_TOP_ROW_CLASS_NAME } from './const';

export type AllowedArtifactType = typeof IngestionArtifactType.Article | typeof IngestionArtifactType.Fragment;

export interface IArtifactListTitleProps {
  artifactType: AllowedArtifactType;
  resource?: IngestionResourceModel;
  renderMode: ContentRenderMode;
  onOpen?: (
    resource: IngestionResourceModel,
    artifactType: IngestionArtifactType,
    oldArtifactType?: IngestionArtifactType
  ) => void;
  onRenderModeChange: (value: string) => void;
  onPreviousResourceClick: () => void;
  onNextResourceClick: () => void;
}

const ArtifactListTitle: React.FC<IArtifactListTitleProps> = ({
  artifactType,
  resource,
  renderMode,
  onOpen,
  onRenderModeChange,
  onPreviousResourceClick,
  onNextResourceClick,
}) => {
  const { t } = useTranslation();

  const [menuVisible, setMenuVisible] = useState(false);

  const identity = resource?.identities.latest;
  const parent = identity?.title || identity?.name;
  const current = t('Article list: Title');
  const title = parent ? `${parent} / ${current}` : current;
  const reference = resource?.references.latest;
  const url = reference?.artifact_urls[0]?.public || reference?.file_urls[0]?.public;

  const onButtonClick = () => {
    setMenuVisible(true);
  };

  const onMenuClose = () => {
    setMenuVisible(false);
  };

  const onDownloadSelect = () => {
    // eslint-disable-next-line security/detect-non-literal-fs-filename
    url && window.open(url, '_blank');
  };

  const newArtifactType =
    artifactType === IngestionArtifactType.Article ? IngestionArtifactType.Fragment : IngestionArtifactType.Article;

  const onFragmentsSelect = () => {
    resource && onOpen?.(resource, newArtifactType, artifactType);
  };

  const onDocumentsSelect = () => {
    resource && onOpen?.(resource, IngestionArtifactType.Document, artifactType);
  };

  const menuItems = [
    {
      icon: <IbIcon iconName="download" size={20} />,
      text: t('Resource table: Context: Download'),
      onSelect: onDownloadSelect,
      disabled: !url,
    },
    {
      icon: <IbIcon iconName="view-list" size={20} />,
      text:
        artifactType === IngestionArtifactType.Article
          ? t('Resource table: Context: Open fragments')
          : t('Resource table: Context: Open articles'),
      onSelect: onFragmentsSelect,
      disabled: !resource,
    },
    {
      icon: <IbIcon iconName="web-page" size={20} />,
      text: t('Resource table: Context: Open documents'),
      onSelect: onDocumentsSelect,
      disabled: identity?.mime !== TEXT_HTML_MIME,
    },
  ];

  return (
    <div className={MAIN_CLASS_NAME}>
      <div className={TITLE_TOP_ROW_CLASS_NAME}>
        <span>{title}</span>
        <IbContextMenu menuItems={menuItems} visible={menuVisible} onClose={onMenuClose}>
          <IbButton icon={<IbIcon iconName="more-one" />} type="icon" onClick={onButtonClick} />
        </IbContextMenu>
        <div className="controls">
          <IbButton icon={<IbIcon iconName="left" />} type="icon" onClick={onPreviousResourceClick} />
          <IbButton icon={<IbIcon iconName="right" />} type="icon" onClick={onNextResourceClick} />
        </div>
      </div>
      <div>
        <Switcher
          options={[
            { value: ContentRenderMode.MARKDOWN, label: t('Artifact list: View mode: Markdown') },
            { value: ContentRenderMode.PLAINTEXT, label: t('Artifact list: View mode: Text') },
            { value: ContentRenderMode.BOTH, label: t('Artifact list: View mode: Both') },
          ]}
          value={renderMode}
          onChange={onRenderModeChange}
        />
      </div>
    </div>
  );
};

export default ArtifactListTitle;
